import React from 'react';
import config from '../config/config';
import { resetMapToNorth } from '../services/mapService';
import { takeMapSnapshot } from '../services/snapshotService';
import { getCurrentLocation } from '../services/currentLocationService';
import { toggle3dMode } from '../services/mode3dService';

const ICON_URLS = {
  print: `${config.icons.baseUrl}${config.icons.print}`,
  capture: `${config.icons.baseUrl}${config.icons.capture}`,
  cube: `${config.icons.baseUrl}${config.icons.cube}`,
  layers: `${config.icons.baseUrl}${config.icons.layers}`,
  nearMe: `${config.icons.baseUrl}${config.icons.nearMe}`,
  nearMeFill: `${config.icons.baseUrl}${config.icons.nearMeFill}`,
  plus: `${config.icons.baseUrl}${config.icons.plus}`,
  minus: `${config.icons.baseUrl}${config.icons.minus}`,
};

const SNAPSHOT_FLASH_DURATION_MS = 300; // スナップショットフラッシュの継続時間
const DESCRIPTION_COMPASS_RESET = '北向きに戻す';
const DESCRIPTION_3D_TOGGLE = '傾きを戻す';
const DESCRIPTION_ZOOM_IN = '拡大';
const DESCRIPTION_ZOOM_OUT = '縮小';
const DESCRIPTION_PRINT = '印刷';
const DESCRIPTION_SNAPSHOT = 'スナップショット保存';
const DESCRIPTION_LAYER_TOGGLE = '背景地図の切替';
const DESCRIPTION_CURRENT_LOCATION = '現在地へ移動';
const BUTTON_STATES_LOCATED = 'located';
const COMPASS_BEARING_THRESHOLD = 0.1;

/**
 * 画面右下のボタン群コンポーネント
 */
const MapControls = ({
  map,
  onZoomInClick,
  onZoomOutClick,
  isLayerPopupOpen, // レイヤーポップアップの表示状態
  setIsLayerPopupOpen, // レイヤーポップアップの表示状態更新関数
  setIsPrintPopupOpen, // 印刷ポップアップの表示状態更新関数
  currentLocationButtonState, // 現在地ボタンの状態
  setCurrentLocationButtonState, // 現在地ボタンの状態更新関数
  pitch, // 現在の傾き
  bearing, // 現在の方位
  is3dMode, // 3Dモードの状態
  setIs3dMode, // 3Dモードの状態更新関数
  setHasMapMoved, // 地図が移動したかどうかの状態更新関数
  isShowNavi,
}) => {

  // コンパスボタンの表示判定
  const showCompass = Math.abs(bearing) >= COMPASS_BEARING_THRESHOLD;

  // 3Dボタンの表示判定
  const show3dButton = Math.abs(pitch) !== 0;

  // 印刷ポップアップの表示
  const handlePrintClick = () => {
    setIsPrintPopupOpen(true);
  };

  // スナップショット保存
  const handleSnapshotClick = () => {
    takeMapSnapshot(map).catch(() => {
      // 出力処理なし
    });

    // フラッシュ効果を追加
    const flashElement = document.createElement('div');
    flashElement.className = 'snapshot-flash';
    document.body.appendChild(flashElement);
    setTimeout(() => {
      document.body.removeChild(flashElement);
    }, SNAPSHOT_FLASH_DURATION_MS);
  };

  // 3Dモード切替
  const handle3dModeClick = () => {
    // ph1では、傾きを戻すために配置するので、初期状態がオン、押下時に3Dモードがオフになるようにする
    setIs3dMode(false);
    toggle3dMode(map, false);
  };

  // レイヤーポップアップの表示・非表示切替
  const handleLayerClick = () => {
    // 現在の状態を反転
    setIsLayerPopupOpen(!isLayerPopupOpen);
  };

  // 現在地
  const handleCurrentLocationClick = () => {
    getCurrentLocation(map, setCurrentLocationButtonState, setHasMapMoved);
  };

  return (
    // モバイル端末で非表示にするボタンについてはmap.cssで指定(max-width: 600px）
    <div className={`map-controls ${isShowNavi? "navi-layout": ""}`}>{/* naviが表示しているときはnavi-layoutのclassを付与して検索結果と被らないようにする */}

      {/* コンパスボタン */}
      {showCompass && (
        <button
          id="compass-btn"
          className="map-control-btn compass"
          title={DESCRIPTION_COMPASS_RESET}
          aria-label={DESCRIPTION_COMPASS_RESET}
          onClick={resetMapToNorth.bind(null, map, is3dMode, setIs3dMode)}
        >
          {/* svgの内部構造はcss移植せずjsxに直接記述 */}
          <svg className="compass-icon"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            style={{ transform: `rotate(${-bearing}deg)` }}
          >
            {/* コンパスの針（赤い部分） */}
            <path className="compass-arrow" d="M12 2L8 11h8l-4-9z" fill="#d00" />
            {/* コンパスの基部（青い部分） */}
            <path d="M12 22l4-9H8l4 9z" fill="#369" />
          </svg>
        </button>
      )}

      {/* 3Dモード切替ボタン ph1では傾きを戻すために配置するので、傾きが生じた時点でオン状態で表示 */}
      {show3dButton && (
        <button
          id="3d-toggle-btn"
          // .map-control-btn.active でアクティブ状態のスタイルを適用
          className={`map-control-btn active`}
          title={DESCRIPTION_3D_TOGGLE}
          aria-label={DESCRIPTION_3D_TOGGLE}
          onClick={handle3dModeClick}
          aria-pressed={is3dMode}
        >
          <i
            className="svg-icon-blue"
            style={{ backgroundImage: `url('${ICON_URLS.cube}')` }}
          ></i>
        </button>
      )}

      {/* ズームイン・アウトボタン */}
      <div className="zoom-controls">
        <button
          id="zoom-in-btn"
          className="map-control-btn"
          title={DESCRIPTION_ZOOM_IN}
          aria-label={DESCRIPTION_ZOOM_IN}
          onClick={onZoomInClick}
        >
          <i
            className="svg-icon-blue"
            style={{ backgroundImage: `url('${ICON_URLS.plus}')` }}
          ></i>
        </button>
        <button
          id="zoom-out-btn"
          className="map-control-btn"
          title={DESCRIPTION_ZOOM_OUT}
          aria-label={DESCRIPTION_ZOOM_OUT}
          onClick={onZoomOutClick}
        >
          <i
            className="svg-icon-blue"
            style={{ backgroundImage: `url('${ICON_URLS.minus}')` }}
          ></i>
        </button>
      </div>

      {/* 印刷ボタン 右クリックメニューにも有 */}
      <button
        id="print-btn"
        className="map-control-btn"
        title={DESCRIPTION_PRINT}
        aria-label={DESCRIPTION_PRINT}
        onClick={handlePrintClick}
      >
        <i
          className="svg-icon-blue"
          style={{ backgroundImage: `url('${ICON_URLS.print}')` }}
        ></i>
      </button>

      {/* スナップショットボタン 右クリックメニューにも有 */}
      <button
        id="snapshot-btn"
        className="map-control-btn"
        title={DESCRIPTION_SNAPSHOT}
        aria-label={DESCRIPTION_SNAPSHOT}
        onClick={handleSnapshotClick}
      >
        <i
          className="svg-icon-blue"
          style={{ backgroundImage: `url('${ICON_URLS.capture}')` }}
        ></i>
      </button>

      {/* レイヤー切替ボタン */}
      <button
        id="layer-toggle-btn"
        className="map-control-btn"
        title={DESCRIPTION_LAYER_TOGGLE}
        aria-label={DESCRIPTION_LAYER_TOGGLE}
        onClick={handleLayerClick}
      >
        <i
          className="svg-icon-blue"
          style={{ backgroundImage: `url('${ICON_URLS.layers}')` }}
        ></i>
      </button>

      {/* 現在地ボタン */}
      <button
        id="current-location-btn"
        className="map-control-btn"
        title={DESCRIPTION_CURRENT_LOCATION}
        aria-label={DESCRIPTION_CURRENT_LOCATION}
        data-state={currentLocationButtonState}
        onClick={handleCurrentLocationClick}
      >
        <i
          className="svg-icon-blue"
          style={{
            backgroundImage:
              currentLocationButtonState === BUTTON_STATES_LOCATED
                ? `url('${ICON_URLS.nearMeFill}')`
                : `url('${ICON_URLS.nearMe}')`,
          }}
        ></i>
      </button>

    </div>
  );
};

export default MapControls;